import { useRef, useState } from "react";

import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Layout,
  message,
  Row,
  Space,
} from "antd";

import { PrinterOutlined, TableOutlined } from "@ant-design/icons";
import axios from "axios";
import { sumBy } from "lodash";
import moment from "moment";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import ReactToPrint from "react-to-print";
import RangeDatePickerFieldGroup from "../../commons/RangeDatePickerFieldGroup";
import numberFormat from "../../utils/numberFormat";
import { smallFormItemLayout } from "./../../utils/Layouts";

const { Content } = Layout;

const transaction = "delivery-receipts";
const url = `/api/${transaction}/`;
const title = "Deliveries Report";

const initialValues = {
  period_covered: [moment(), moment()],
};
export default function DeliveriesReport() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const [branches, setBranches] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [options, setOptions] = useState({
    customers: [],
  });
  const [loading, setLoading] = useState({
    search: false,
    download: false,
  });

  const report = useRef(null);

  const getReport = useCallback(({ period_covered, is_excel = false }) => {
    if (period_covered) {
      const form_data = {
        period_covered,
        is_excel,
      };

      setLoading((prevState) => ({
        ...prevState,
        search: !is_excel,
        download: is_excel,
      }));

      axios
        .post(`${url}deliveries-report`, form_data)
        .then((response) => {
          if (response.data) {
            if (is_excel) {
              setLoading((prevState) => ({
                ...prevState,
                download: false,
              }));
              return window.open(`/public/reports/${response.data.filename}`);
            }

            setRecords(response.data.records);
            setBranches(response.data.branches);
            setLoading((prevState) => ({
              ...prevState,
              search: false,
            }));
          }
        })
        .catch((err) => {
          message.error("There was an error processing your request");
        })
        .finally(() =>
          setLoading((prevState) => ({
            ...prevState,
            search: false,
            download: false,
          }))
        );
    } else {
      message.error("Please select period covered");
    }
  }, []);

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item className="text-color-yellow">
              {title}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          padding: 24,
          minHeight: 280,
          borderRadius: 10,
        }}
      >
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <RangeDatePickerFieldGroup
              label="Period Covered"
              name="period_covered"
              value={state.period_covered}
              onChange={(dates) =>
                setState((prevState) => ({
                  ...prevState,
                  period_covered: dates,
                }))
              }
              error={errors.period_covered}
              formItemLayout={smallFormItemLayout}
            />
          </Col>
        </Row>

        <Row>
          <Col span={12} offset={4}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  onClick={() => {
                    getReport({
                      ...state,
                    });
                  }}
                  loading={loading.search}
                  disabled={loading.search}
                >
                  Search
                </Button>
                <Button
                  icon={<TableOutlined />}
                  onClick={() => {
                    getReport({
                      ...state,
                      is_excel: true,
                    });
                  }}
                  loading={loading.download}
                  disabled={loading.download}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div>
            <div className="has-text-weight-bold">{title}</div>
            <div>
              Period Covered: {state.period_covered?.[0]?.format("MM/DD/YYYY")}{" "}
              to {state.period_covered?.[1]?.format("MM/DD/YYYY")}
            </div>
          </div>
          <table className="full-width sales-report-table m-t-1">
            <thead>
              <tr>
                <th>ITEM DESCRIPTIONS</th>
                {branches.map((branch) => [
                  <th className="has-text-right" colSpan={2}>
                    {branch}
                  </th>,
                ])}
                <th className="has-text-right" colSpan={2}>
                  TOTAL PURCHASE
                </th>
              </tr>
              <tr>
                <th />
                {branches.map((branch) => [
                  <th className="has-text-right">QTY</th>,
                  <th className="has-text-right">AMOUNT</th>,
                ])}
                <th className="has-text-right">QTY</th>
                <th className="has-text-right">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => [
                <tr>
                  <td className="has-text-weight-bold">{record.category}</td>
                  <td colSpan={(branches.length - 1) * 2 + 4} />
                </tr>,
                record.stocks.map((stock) => (
                  <tr>
                    <td>{stock.name}</td>
                    {stock.stock_branches.map((o) => [
                      <td className="has-text-right">{o.quantity}</td>,
                      <td className="has-text-right">
                        {o?.amount ? numberFormat(o?.amount) : ""}
                      </td>,
                    ])}
                    <td className="has-text-right has-text-weight-bold">
                      {sumBy(stock.stock_branches, (o) => o.quantity)}
                    </td>
                    <td className="has-text-right has-text-weight-bold">
                      {numberFormat(
                        sumBy(stock.stock_branches, (o) => o.amount)
                      )}
                    </td>
                  </tr>
                )),
              ])}
            </tbody>
            {/* <tfoot
              className="has-text-weight-bold"
              style={{ borderTop: "2px solid #333" }}
            >
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tfoot> */}
          </table>
        </div>
      </div>
    </Content>
  );
}
